import * as turf from '@turf/turf'
import type { GuidanceFieldFeatures } from 'api-validations/geojson-schemas'
import { guidancePathApi } from 'api-validations/helm/guidance-path'
import {
  DEFAULT_GLOBAL_SETTINGS,
  PropagationMode,
  type PropagatableGuidancePrimitive,
  propagatableGuidancePrimitiveSchema,
  type RegionBoundedHelmRequest
} from 'api-validations/locutus/guidance'
import { keepPreviousData, usePanoramaQuery } from 'shared-ui/api-provider'
import { NO_PROPAGATION_REQUEST, toPropagationBoundary, withSwathWidth } from 'shared-ui/map'

// Widest anyone would reasonably place a guidance point off the field. Fields
// max out at a few km on a side, and inputs that far off are almost always
// the initial-sketch state (a lat/lng typed only partway) — not a real
// placement helm should waste a request on.
const MAX_PRIMITIVE_DISTANCE_KM = 50

const isPrimitiveNearField = (
  primitive: PropagatableGuidancePrimitive,
  fieldFeatures: GuidanceFieldFeatures
): boolean => {
  const center = turf.center(fieldFeatures)
  const geometry = primitive.primitive.geometry
  const coords: Array<[number, number]> =
    geometry.type === 'Point'
      ? [geometry.coordinates as [number, number]]
      : (geometry.coordinates as Array<[number, number]>)
  return coords.every(
    (c) =>
      turf.distance(center, turf.point(c), { units: 'kilometers' }) <= MAX_PRIMITIVE_DISTANCE_KM
  )
}

export const buildLinePreviewRequest = ({
  fieldFeatures,
  primitive,
  swathWidthFt
}: {
  fieldFeatures: GuidanceFieldFeatures
  primitive: PropagatableGuidancePrimitive
  swathWidthFt: number
}): RegionBoundedHelmRequest | null => {
  // The primitive schema already refines the degenerate initial-sketch shapes
  // helm rejects: ABLine with pointA === pointB, RadialPivot with radius 0.
  // The distance check catches the other initial-sketch failure mode: a
  // schema-valid primitive whose coordinates are still pinned near (0, 0) (or
  // a half-typed lat/lng) and therefore nowhere near the field.
  if (!propagatableGuidancePrimitiveSchema.safeParse(primitive).success) return null
  if (!isPrimitiveNearField(primitive, fieldFeatures)) return null
  if (!Number.isFinite(swathWidthFt)) return null
  const propagationBoundary = toPropagationBoundary(fieldFeatures)
  if (propagationBoundary.features.length === 0) return null
  return {
    type: PropagationMode.RegionBounded,
    fieldFeatures: propagationBoundary,
    globalSettings: DEFAULT_GLOBAL_SETTINGS,
    overrides: {},
    infillRegions: [{ infill: withSwathWidth({ infill: primitive, swathWidthFt }) }]
  }
}

export const useHelmPath = (request: RegionBoundedHelmRequest | null) =>
  usePanoramaQuery(
    guidancePathApi,
    undefined,
    {
      enabled: request !== null,
      placeholderData: keepPreviousData,
      staleTime: Number.POSITIVE_INFINITY
    },
    request ?? NO_PROPAGATION_REQUEST
  )
